import React, { useState, useEffect, useRef } from 'react';
import { UserPlus, UserCheck, UserX, Loader2 } from 'lucide-react';
import { checkIsFollowing, followUser, unfollowUser } from '../../services/followService';

interface FollowButtonProps {
  currentUserName?: string;
  targetUserName: string;
  size?: 'sm' | 'md';
  onFollowChange?: (isFollowing: boolean) => void;
}

export const FollowButton: React.FC<FollowButtonProps> = ({
  currentUserName = '',
  targetUserName,
  size = 'sm',
  onFollowChange
}) => {
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const isCheckingRef = useRef<boolean>(false);

  const cleanCurrent = currentUserName?.trim();
  const cleanTarget = targetUserName?.trim();
  const isSelf = cleanCurrent.toLowerCase() === cleanTarget.toLowerCase();

  // Check follow status on mount
  useEffect(() => {
    if (!cleanCurrent || !cleanTarget || isSelf) {
      setIsFollowing(false);
      return;
    }

    if (isCheckingRef.current) return;
    isCheckingRef.current = true;

    checkIsFollowing(cleanCurrent, cleanTarget)
      .then((following) => {
        setIsFollowing(following);
      })
      .catch(() => {})
      .finally(() => {
        isCheckingRef.current = false;
      });
  }, [cleanCurrent, cleanTarget, isSelf]);

  if (!cleanCurrent || !cleanTarget || isSelf) {
    return null;
  }

  const handleToggleFollow = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isLoading) return;

    setIsLoading(true);
    const nextState = !isFollowing;

    // Optimistic UI update
    setIsFollowing(nextState);

    try {
      if (nextState) {
        const res = await followUser(cleanCurrent, cleanTarget);
        if (!res.success) {
          setIsFollowing(false); // rollback
        } else {
          onFollowChange?.(true);
        }
      } else {
        const res = await unfollowUser(cleanCurrent, cleanTarget);
        if (!res.success) {
          setIsFollowing(true); // rollback
        } else {
          onFollowChange?.(false);
        }
      }
    } catch {
      setIsFollowing(!nextState); // rollback on crash
    } finally {
      setIsLoading(false);
    }
  };

  const isSm = size === 'sm';
  const padding = isSm ? '3px 8px' : '5px 12px';
  const fontSize = isSm ? '0.70rem' : '0.78rem';
  const iconSize = isSm ? 11 : 13;

  // Unfollow hover style
  if (isFollowing) {
    return (
      <button
        type="button"
        onClick={handleToggleFollow}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={isLoading}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding,
          fontSize,
          fontWeight: 600,
          borderRadius: '9999px',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          border: isHovered
            ? '1px solid rgba(239, 68, 68, 0.5)'
            : '1px solid rgba(168, 85, 247, 0.4)',
          background: isHovered
            ? 'rgba(239, 68, 68, 0.15)'
            : 'rgba(168, 85, 247, 0.15)',
          color: isHovered ? '#fca5a5' : '#e9d5ff',
          backdropFilter: 'blur(8px)',
          outline: 'none'
        }}
        title={isHovered ? `Unfollow @${cleanTarget}` : `Following @${cleanTarget}`}
      >
        {isLoading ? (
          <Loader2 size={iconSize} className="spin" />
        ) : isHovered ? (
          <UserX size={iconSize} />
        ) : (
          <UserCheck size={iconSize} color="#c084fc" />
        )}
        <span>{isLoading ? '...' : isHovered ? 'Unfollow' : 'Following'}</span>
      </button>
    );
  }

  // Follow inactive button
  return (
    <button
      type="button"
      onClick={handleToggleFollow}
      disabled={isLoading}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding,
        fontSize,
        fontWeight: 600,
        borderRadius: '9999px',
        cursor: isLoading ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        border: '1px solid rgba(168, 85, 247, 0.5)',
        background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.25) 0%, rgba(99, 102, 241, 0.2) 100%)',
        color: '#f3e8ff',
        backdropFilter: 'blur(8px)',
        outline: 'none',
        boxShadow: '0 2px 8px rgba(168, 85, 247, 0.2)'
      }}
      title={`Follow @${cleanTarget}`}
    >
      {isLoading ? (
        <Loader2 size={iconSize} className="spin" />
      ) : (
        <UserPlus size={iconSize} color="#c084fc" />
      )}
      <span>{isLoading ? '...' : '+ Follow'}</span>
    </button>
  );
};
